/**
 * IA Cart Upsell - Re-renders when first cart item's product ID changes
 */

import { EVENTS, subscribe } from "ia-util-pubsub";

// API handler for fetching product recommendations
class RecommendationsAPI {
  static async fetch(productId, { limit, intent, sectionId }) {
    if (!productId) return [];
    
    try {
      const url = `${window.Shopify.routes.root}recommendations/products?product_id=${productId}&limit=${limit}&intent=${intent}&section_id=${sectionId}`;
      const response = await fetch(url);
      if (!response.ok) return [];

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const container = doc.querySelector('.ia-cart-upsell__container');
      return container ? Array.from(container.querySelectorAll('.ia-cart-upsell__item-wrapper')) : [];
    } catch (error) {
      console.error('Error loading recommendations:', error);
      return [];
    }
  }
}

if (!customElements.get('ia-cart-upsell')) {

  class IaCartUpsell extends HTMLElement {
    constructor() {
      super();
      this.baseProductId = this.dataset.baseProductId || '';
      this.cartProductIds = [];
      this.excludedProductIds = [];
      this.enableDynamic = this.dataset.enableDynamic === 'true';
      this.priorManualProducts = this.dataset.priorManualProducts === 'true';
      this.limit = this.dataset.limit ? parseInt(this.dataset.limit) : 12;
      this.intent = this.dataset.intent || 'complementary';
      this.sectionId = this.dataset.sectionId || 'ia-recommendations';
      this.containerTemplate = null;
    }

    connectedCallback() {
      this.cartProductIds = this.getUniqueIds((this.dataset.cartProductIds || '').split(',').filter(id => id.trim()));
      this.excludedProductIds = this.getUniqueIds((this.dataset.excludedProductIds || '').split(',').filter(id => id.trim()));
      
      // Save the original container template
      const container = this.querySelector('.ia-cart-upsell__container');
      if (container) {
        this.containerTemplate = container.outerHTML;
      }

      this.render();
      this.unsubscribe = subscribe(EVENTS.cartChanged, this.handleCartChanged.bind(this));
    }

    disconnectedCallback() {
      this.unsubscribe?.();
    }

    getUniqueIds(ids) {
      return [...new Set(ids.map(id => String(id)))];
    }

    arraysEqual(arr1, arr2) {
      if (arr1.length !== arr2.length) return false;
      const sorted1 = [...arr1].sort();
      const sorted2 = [...arr2].sort();
      return sorted1.every((val, index) => val === sorted2[index]);
    }

    handleCartChanged(event) {
      const { cartObj, cartDom } = event.detail;
      if (!cartObj) return;

      const newCartProductIds = this.getUniqueIds(cartObj.items?.map(item => String(item.product_id)) || []);
      const newBaseProductId = cartObj.items?.[0]?.product_id ? String(cartObj.items[0].product_id) : '';

      // Check if cart products actually changed
      const cartChanged = !this.arraysEqual(this.cartProductIds, newCartProductIds);
      const baseProductChanged = newBaseProductId !== this.baseProductId;

      if (!cartChanged && !baseProductChanged) {
        return; // Nothing changed, skip render
      }

      this.cartProductIds = newCartProductIds;
      this.baseProductId = newBaseProductId;
      this.render(cartDom, cartObj);
    }

    async render(cartDom = null, cartObj = window.iaCartAPI.cart) {
      // console.log('[IA Cart Upsell] Rendering initialization', cartObj);
      if (!cartObj) return;
      if (cartObj?.item_count === 0) {
        this.classList.add('ia-hidden');
        return;
      }

      this.classList.add('ia-hidden');

      if (this.priorManualProducts) {
        // Prioritize manual products over API recommendations
        const manualItems = this.getManualItems(cartDom);
        this.rebuildContainer([...manualItems]);
        manualItems.length > 0 && this.classList.remove('ia-hidden');
      } else {
        // Prioritize API recommendations over manual products
        const apiItems = this.enableDynamic && this.baseProductId 
        ? await RecommendationsAPI.fetch(this.baseProductId, {
            limit: parseInt(this.dataset.limit) || 10,
            intent: this.dataset.intent || 'complementary',
            sectionId: this.dataset.sectionId
          })
        : [];
        this.rebuildContainer([...apiItems]);
        apiItems.length > 0 && this.classList.remove('ia-hidden');
      }

    }

    getManualItems(cartDom) {
      const source = cartDom?.querySelector('ia-cart-upsell') || this;
      return Array.from(source.querySelectorAll('.ia-cart-upsell__item-wrapper'));
    }

    rebuildContainer(items) {
      const filtered = items.filter(item => {
        const productId = item.dataset.productId;
        if (!productId) return false;
        const productIdStr = String(productId);
        // Exclude products that are in cart or in excluded list
        return !this.cartProductIds.includes(productIdStr) && !this.excludedProductIds.includes(productIdStr);
      });


      // Limit to max 2 items (prioritizing manual products which come first)
      const limitedItems = filtered.slice(0, 2);

      // Get the container
      const container = this.querySelector('.ia-cart-upsell__container');
      if (container) {
        container.innerHTML = '';
        limitedItems.forEach(item => container.appendChild(item.cloneNode(true)));
      }
    }
  }

  customElements.define('ia-cart-upsell', IaCartUpsell);
}
